import React, { useEffect, useState } from "react";
import HudCorners from "./HudCorners";
import { api } from "../lib/api";
import { Globe, Info } from "lucide-react";

/**
 * MacroBriefing — Morning-briefing hero card.
 *
 * Reads the world before we ever pick a stock. Shows regime, Fear/Greed gauge,
 * a synthesized narrative, and the 4-5 most important flags right now (VIX,
 * yield curve, tape, F&G, oil-if-stressed). Also surfaces the scanner modifier
 * so subscribers know why the feed is quiet/aggressive.
 *
 * Data: /api/macro (public, 15-min cached)
 */
export default function MacroBriefing() {
  const [macro, setMacro] = useState(null);
  const [err, setErr] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchMacro = React.useCallback(async ({ fresh = false } = {}) => {
    try {
      if (fresh) setRefreshing(true);
      const r = await api.get(fresh ? "/macro?fresh=1" : "/macro");
      setMacro(r.data);
      setErr(false);
    } catch {
      setErr(true);
    } finally {
      if (fresh) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    fetchMacro().finally(() => { if (!alive) return; });
    // Refresh every 3 minutes so an open dashboard stays fresh without user action
    const interval = setInterval(() => { if (alive) fetchMacro(); }, 180000);
    // Listen for global "mb:refresh-tape" so the Dashboard Refresh button also
    // forces a fresh macro synth alongside sectors + alert feed.
    const onGlobalRefresh = () => fetchMacro({ fresh: true });
    window.addEventListener("mb:refresh-tape", onGlobalRefresh);
    return () => {
      alive = false;
      clearInterval(interval);
      window.removeEventListener("mb:refresh-tape", onGlobalRefresh);
    };
  }, [fetchMacro]);

  if (!macro) {
    // Skeleton — always render the briefing card even while /api/macro is
    // pending. Off-hours or during a cold-start we DON'T want the section
    // to just disappear.
    return (
      <div className="mb-card p-6 relative opacity-80" data-testid="macro-briefing-skeleton">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <span className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm animate-pulse"
                style={{ background: "var(--mb-surface-elevated)", color: "var(--mb-ink-muted)" }}>
            <Globe className="w-3.5 h-3.5" strokeWidth={2.25} /> Market: —
          </span>
          <div className="text-xs" style={{ color: "var(--mb-ink-muted)" }}>Morning briefing</div>
        </div>
        <div className="mt-6 text-sm" style={{ color: "var(--mb-ink-muted)" }}>Fear / Greed</div>
        <div className="font-serif-hero text-6xl leading-none mt-1 animate-pulse" style={{ color: "var(--mb-ink-subtle)" }}>—</div>
        <div className="mt-8 h-3 rounded-full opacity-30" style={{ background: "linear-gradient(90deg, #F87171 0%, #F5A623 45%, #A3E635 70%, #34D399 100%)" }} />
        <p className="mt-6 text-sm leading-relaxed max-w-4xl" style={{ color: "var(--mb-ink-muted)" }}>
          {err
            ? "Couldn't reach macro data right now — refresh in a moment. The desk is still watching the tape."
            : "Reading the market — synthesizing macro indicators, breadth, VIX, and Fear/Greed…"}
        </p>
      </div>
    );
  }

  const regime = macro.regime || "NEUTRAL";
  const cfg = REGIME_CFG[regime] || REGIME_CFG.NEUTRAL;
  const fg = macro.fear_greed_score;

  return (
    <div className="mb-card p-6 relative" data-testid="macro-briefing">
      <HudCorners color={cfg.hud} />

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <span
          className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-semibold"
          style={{ backgroundColor: `${cfg.bg}1F`, color: cfg.bg, border: `1px solid ${cfg.bg}66` }}
          title="Composite of macro indicators + CNN Fear/Greed"
          data-testid="macro-regime-badge"
        >
          <Globe className="w-3.5 h-3.5" strokeWidth={2.25} />
          {regime === "RISK-ON" ? "Buy regime" : `Market: ${regime.charAt(0) + regime.slice(1).toLowerCase()}`}
        </span>
        <div className="text-xs" style={{ color: "var(--mb-ink-muted)" }}>
          Morning briefing · refreshed {new Date(macro.computed_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>

      <FearGreedPanel score={fg} bands={macro.fg_bands || FG_BANDS} />

      {/* Narrative — expands to full card width so the LLM pulse doesn't get
          squeezed into a narrow column when the top row grows on wide screens. */}
      <p className="mt-5 text-sm leading-relaxed w-full" style={{ color: "var(--mb-ink-muted)" }} data-testid="macro-narrative">
        {macro.narrative}
      </p>

      {/* Flags — up to 5 key contextual chips */}
      <div className="mt-4 flex flex-wrap gap-2" data-testid="macro-flags">
        {(macro.flags || []).slice(0, 6).map((f, i) => (
          <span
            key={i}
            className="font-mono-data text-[11px] px-2.5 py-1 rounded border"
            style={{
              color: "var(--mb-ink)",
              backgroundColor: "var(--mb-surface-elevated)",
              borderColor: "var(--mb-border)",
              letterSpacing: "0.03em",
            }}
          >
            {f}
          </span>
        ))}
      </div>

      {/* Scanner modifier — what the algo is doing right now */}
      <div
        className="mt-5 pt-4 border-t flex flex-wrap items-center justify-between gap-3"
        style={{ borderColor: "var(--mb-border)" }}
      >
        <div className="flex items-center gap-2">
          <Info className="w-3.5 h-3.5" style={{ color: "var(--mb-emerald)" }} />
          <span className="label-uppercase" style={{ color: "var(--mb-ink-subtle)", letterSpacing: "0.16em" }}>
            SCANNER MODE
          </span>
        </div>
        <div className="flex flex-wrap gap-4 font-mono-data text-[11px]" style={{ color: "var(--mb-ink-muted)" }}>
          <span>Moonshots: <span style={{ color: "var(--mb-ink)" }}>{macro.modifier?.moonshot_threshold}</span></span>
          <span>Steady: <span style={{ color: "var(--mb-ink)" }}>{macro.modifier?.steady_threshold}</span></span>
          <span>Pace: <span style={{ color: "var(--mb-ink)" }}>{macro.modifier?.publish_pace}</span></span>
        </div>
      </div>
    </div>
  );
}

// ─── Fear/Greed full-width gauge with regime bands ─────────────
const FG_BANDS = { risk_off_max: 35, cautious_max: 55, buy_min: 75 };

function FearGreedPanel({ score, bands }) {
  const pct = Math.max(0, Math.min(100, score ?? 50));
  const inBuy = Math.round(pct) >= bands.buy_min;
  const zones = [
    { label: "Risk-off", from: 0, to: bands.risk_off_max },
    { label: "Cautious", from: bands.risk_off_max, to: bands.cautious_max },
    { label: "Neutral", from: bands.cautious_max, to: bands.buy_min },
    { label: "Buy zone", from: bands.buy_min, to: 100, buy: true },
  ];
  return (
    <div className="mt-6" data-testid="fear-greed-panel">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm" style={{ color: "var(--mb-ink-muted)" }}>Fear / Greed</div>
          <div className="font-serif-hero text-6xl leading-none mt-1 tabular-nums" style={{ color: fgColor(pct) }} data-testid="fear-greed-score">
            {score?.toFixed(0)}<span className="text-2xl" style={{ color: "var(--mb-ink-subtle)" }}> / 100</span>
          </div>
          <div className="mt-1 text-xs" style={{ color: "var(--mb-ink-subtle)" }}>Moonbucks composite of macro, breadth, VIX and CNN Fear/Greed</div>
        </div>
      </div>

      <div className="relative mt-9" title={`Fear/Greed ${pct.toFixed(0)}/100 — 0 extreme fear, 100 extreme greed`}>
        <div className="absolute -top-7 right-0 sm:right-auto text-xs font-semibold text-emerald-300 flex items-center gap-1.5 whitespace-nowrap"
             style={{ left: window.innerWidth < 640 ? undefined : `${bands.buy_min}%` }} data-testid="fear-greed-buy-label">
          <span className="w-px h-3 bg-emerald-300/70" /> Buy territory starts at {bands.buy_min}
        </div>
        <div className="h-3 rounded-full" style={{ background: "linear-gradient(90deg, #F87171 0%, #F5A623 45%, #A3E635 70%, #34D399 100%)", opacity: 0.85 }} />
        <div className="absolute -inset-y-1 rounded-r-full border-2 border-emerald-300/80"
             style={{ left: `${bands.buy_min}%`, right: -2, boxShadow: inBuy ? "0 0 0 3px rgba(52,211,153,0.18)" : "none" }} data-testid="fear-greed-buy-zone" />
        {[bands.risk_off_max, bands.cautious_max].map((t) => (
          <span key={t} className="absolute top-0 h-3 w-px bg-black/50" style={{ left: `${t}%` }} />
        ))}
        <span className="absolute -top-1.5 w-6 h-6 rounded-full border-[3px] border-white"
              style={{ left: `calc(${pct}% - 12px)`, backgroundColor: fgColor(pct) }} data-testid="fear-greed-marker" />
      </div>

      <div className="relative mt-3 h-5 text-xs" style={{ color: "var(--mb-ink-subtle)" }}>
        {zones.map((z) => (
          <span key={z.label} className={`absolute text-center ${z.buy ? "text-emerald-300 font-semibold" : ""}`}
                style={{ left: `${z.from}%`, width: `${z.to - z.from}%` }}>
            {z.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function fgColor(score) {
  if (score == null) return "#8CB8FF";
  if (score >= 75) return "#4ADE80";
  if (score >= 55) return "#86EFAC";
  if (score >= 40) return "#F5A623";
  if (score >= 25) return "#FF8A5B";
  return "#FF5C5C";
}

const REGIME_CFG = {
  "RISK-ON":   { bg: "#4ADE80", hud: "rgba(74, 222, 128, 0.35)" },
  "NEUTRAL":   { bg: "#C084FC", hud: "rgba(56, 231, 163, 0.55)" },
  "CAUTIOUS":  { bg: "#F5A623", hud: "rgba(245, 166, 35, 0.6)" },
  "RISK-OFF":  { bg: "#FF5C5C", hud: "rgba(255, 92, 92, 0.65)" },
};
